import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps } from "react"
import { cn } from "~/lib/utils"

const cardVariants = cva("text-text-main flex flex-col rounded-xl", {
	variants: {
		density: {
			default: "gap-6 py-6",
			compact: "gap-4 p-4",
			tight: "gap-3 rounded-lg p-3",
			flush: "gap-0",
			metric: "gap-0 px-4 py-3"
		},
		variant: {
			outline: "border border-stroke bg-background-secondary",
			dashed: "border border-dashed border-stroke bg-background-secondary",
			primary: "bg-background-secondary surface-raised",
			secondary: "bg-background-tertiary surface-recessed",
			interactive:
				"bg-background-secondary surface-raised transition-[background-color,border-color,box-shadow,translate] duration-100 hover:-translate-y-0.5 hover:border-stroke-hover hover:bg-background-tertiary hover:shadow-floating motion-reduce:transition-none"
		}
	},
	defaultVariants: {
		variant: "primary",
		density: "default"
	}
})

function Card({
	className,
	variant,
	density,
	...props
}: ComponentProps<"div"> & VariantProps<typeof cardVariants>) {
	return (
		<div
			data-slot="card"
			className={cn(cardVariants({ variant, density }), className)}
			{...props}
		/>
	)
}

function CardHeader({ className, ...props }: ComponentProps<"div">) {
	return (
		<div
			data-slot="card-header"
			className={cn("flex flex-col gap-1.5 px-6", className)}
			{...props}
		/>
	)
}

function CardTitle({ className, ...props }: ComponentProps<"div">) {
	return (
		<div
			data-slot="card-title"
			className={cn("leading-none font-semibold", className)}
			{...props}
		/>
	)
}

function CardDescription({ className, ...props }: ComponentProps<"div">) {
	return (
		<div
			data-slot="card-description"
			className={cn("text-text-small text-sm", className)}
			{...props}
		/>
	)
}

function CardContent({ className, ...props }: ComponentProps<"div">) {
	return (
		<div
			data-slot="card-content"
			className={cn("px-6", className)}
			{...props}
		/>
	)
}

function CardFooter({ className, ...props }: ComponentProps<"div">) {
	return (
		<div
			data-slot="card-footer"
			className={cn("flex items-center px-6", className)}
			{...props}
		/>
	)
}

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle }
